"use client";

import {JSX} from "react";

import {useSnapshot} from "valtio";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons";
import {TextField} from "@/shared/ui/form/fields";

import type {CouponInstance} from "./coupon.model";

import "./styles/coupon.scss";

export interface CouponViewProps {
    model: CouponInstance;
    id?: string;
    className?: string;
}

export function CouponView({model, id = "coupon-code", className = ""}: CouponViewProps): JSX.Element {
    const {t} = useTranslation("booking");
    const snapshot = useSnapshot(model);

    const handleApply = (): void => {
        model.apply();
    };

    const handleClear = (): void => {
        model.clear();
    };

    return (
        <div className={`coupon ${className}`}>
            <label htmlFor={id} className="coupon__label">
                {t("promoCode")}
            </label>
            <div className={`coupon__row${snapshot.isDisabled ? " coupon__row--disabled" : ""}`}>
                <TextField
                    id={id}
                    name="coupon-code"
                    autoComplete="off"
                    model={model.code}
                    placeholder={t("promoCodePlaceholder")}
                    className="coupon__input"
                />
                <ButtonBase
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleApply}
                    loading={snapshot.isApplying}
                    disabled={snapshot.isApplying || !snapshot.codeValue || snapshot.isDisabled}
                    className="coupon__button"
                >
                    {t("applyPromo")}
                </ButtonBase>
            </div>
            {snapshot.isApplied && (
                <div className="coupon__applied" role="status">
                    <p className="coupon__message coupon__message--success">{t("promoApplied")}</p>
                    <button type="button" className="coupon__remove" onClick={handleClear}>
                        {t("promoRemove")}
                    </button>
                </div>
            )}
            {snapshot.hasInfo && snapshot.errorKey && (
                <p className="coupon__message coupon__message--info" role="status">
                    {t(snapshot.errorKey as Parameters<typeof t>[0])}
                </p>
            )}
            {snapshot.hasError && snapshot.errorKey && (
                <p className="coupon__message coupon__message--error" role="alert">
                    {t(snapshot.errorKey as Parameters<typeof t>[0])}
                </p>
            )}
        </div>
    );
}
